import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Package, Truck, Users, FileText,
  Wallet, AlertTriangle, Settings, LogOut, ChevronLeft, ChevronDown, Search, Bell, Warehouse, KeyRound, IndianRupee,
  Scale, Receipt, Calculator, Boxes, Banknote, Code, Smartphone, Menu, UserCircle2,
  Map as SiteMapIcon, ExternalLink, ClipboardList, BarChart3, ShoppingBag, LogIn, LifeBuoy, Mail, UserCog, BookOpen, CalendarDays, Lock,
  Target, Columns3, CalendarClock, Trash2,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, API_BASE_URL } from '@/api/client';
import { GlobalSearch } from './GlobalSearch';
import { ChatbotWidget } from '@/components/ai-chatbot/ChatbotWidget';
import { MarkAttendanceButton } from '@/components/attendance/MarkAttendance';
import { WalletBalanceMenu } from '@/components/layout/WalletBalanceMenu';
import { QuickScanButton } from '@/features/orders/QuickScanButton';
import { useAuth, canModule, canModuleTab, navAllowed, isRestricted } from '@/hooks/useAuth';
import { ThemeToggle } from '@/theme/ThemeToggle';

// permModule: if set, the entry is shown only to SUPER_ADMIN/ADMIN or users whose role grants
// view on that permission module (tab-wise permissions). Used for permission-managed modules (HR).
// permTab: for modules granted TAB-WISE (Leads), the sub-tab this entry needs — checked against
// the same (module, tab) pair the backend route requires, so a visible link never 403s.
// grantOnly: the module must be GRANTED explicitly — a grandfathered role with no permission matrix
// does not get it either (matches a backend route with no `grandfather`), so the link never 403s.
interface NavLeaf { label: string; to: string; icon?: React.ReactNode; superAdminOnly?: boolean; adminOnly?: boolean; permModule?: string; permTab?: string; salesOnly?: boolean; grantOnly?: boolean }
// children may themselves be groups — the sidebar supports nested sections (e.g. Order Management → Orders → B2C/B2B).
interface NavGroup { label: string; icon?: React.ReactNode; children: NavEntry[]; superAdminOnly?: boolean; adminOnly?: boolean; permModule?: string; permTab?: string; salesOnly?: boolean }
type NavEntry = NavLeaf | NavGroup;

function isGroup(e: NavEntry): e is NavGroup { return (e as any).children !== undefined; }
// Every leaf reachable under an entry (used for collapsed mode + active detection).
function leafDescendants(e: NavEntry): NavLeaf[] {
  return isGroup(e) ? e.children.flatMap(leafDescendants) : [e];
}

/**
 * Global header search — finds orders by AWB, LR, order no, customer name/mobile/pincode.
 * Shows a live results dropdown; clicking (or Enter) opens that order's detail page.
 */
// Grouped by operational flow: overview → order lifecycle → network → courier ops →
// finance → stakeholders → system. "Orders" stays a nested sub-section inside Order Management.
const adminNav: NavEntry[] = [
  { label: 'Dashboard', to: '/dashboard', icon: <LayoutDashboard size={18} /> },
  { label: 'User Dashboard', to: '/user-dashboard', icon: <UserCog size={18} />, permModule: 'User Dashboard' },
  { label: 'Reports', to: '/reports', icon: <BarChart3 size={18} /> },
  { label: 'Support Tickets', to: '/tickets', icon: <LifeBuoy size={18} />, permModule: 'Tickets' },
  // Self-service for every employee — raise Asset/Stationery requests + approve ones routed to you.
  // No permModule → always visible (like Dashboard); the page itself handles employee-linkage.
  { label: 'My Requests', to: '/my-requirements', icon: <ClipboardList size={18} /> },
  // Open to every logged-in employee (JWT only, no HR permission) — the same door as My Requests.
  { label: 'Leave', to: '/leave', icon: <CalendarDays size={18} /> },
  // A sales person's own collection sheet. No permModule: the route is scoped to the caller's own
  // clients on the server, and the roles these people hold (Field Executive / Branch Manager) grant
  // no Ledger access at all — opening that module to them would hand over the whole company ledger.
  { label: 'My Collection', to: '/my-collection', icon: <IndianRupee size={18} />, salesOnly: true },
  {
    label: 'Order Management', icon: <Package size={18} />, children: [
      {
        label: 'Orders', icon: <Package size={18} />, children: [
          { label: 'Orders B2C', to: '/orders-b2c', permModule: 'Orders B2C' },
          { label: 'Orders B2B', to: '/orders-b2b', permModule: 'Orders B2B' },
          { label: 'Gaint OFD Orders', to: '/gaint-ofd', permModule: 'Gaint OFD Orders' },
          { label: 'Gaint Appointments', to: '/gaint-appointments', permModule: 'Gaint Appointments' },
          { label: 'RTO Orders', to: '/rto-orders', permModule: 'RTO Orders' },
          { label: 'Orders Bucket', to: '/orders-bucket' },
          { label: 'Appointments', to: '/appointments' },
          { label: 'Tracking', to: '/tracking', permModule: 'Tracking' },
        ],
      },
      { label: 'Test Orders', to: '/test-orders', superAdminOnly: true },
      { label: 'Pickup Requests', to: '/pickup-requests', permModule: 'Pickup Requests' },
      { label: 'NDR', to: '/ndr', permModule: 'NDR' },
      // Call centre. Internal-only by construction: the routes are @Roles(INTERNAL_STAFF) and there
      // is no /ivr route in the merchant app, so a merchant can neither see nor reach it.
      { label: 'IVR', to: '/ivr', permModule: 'IVR' },
      { label: 'EDD Expired', to: '/edd-expired', permModule: 'EDD Expired' },
      { label: 'Weight', to: '/weight', permModule: 'Weight Discrepancy' },
      { label: 'Bulk POD', to: '/pod', permModule: 'Bulk POD' },
      { label: 'Bulk Waybill & Invoices', to: '/b2b-docs', permModule: 'Bulk POD' },
      // MIS data hand-off: filter, then export or email the file to the merchant. Internal only.
      { label: 'Master Data', to: '/master-data', permModule: 'Master Data' },
    ],
  },
  // ONE entry on purpose. WMS is now a drill-down (Warehouse → Merchant → Modules), so the module
  // list lives inside the merchant rather than in the sidebar — a flat menu here would offer screens
  // that cannot answer anything until a warehouse and merchant have been chosen.
  { label: 'WMS', to: '/wms', icon: <Boxes size={18} />, permModule: 'Warehouses' },
  {
    label: 'Network & Warehousing', icon: <Warehouse size={18} />, children: [
      { label: 'Pickup Locations', to: '/warehouses', permModule: 'Pickup Locations' },
      { label: 'Branches', to: '/branches', permModule: 'Branches' },
      { label: 'Serviceability', to: '/serviceability', permModule: 'Serviceability' },
    ],
  },
  {
    label: 'Courier Operations', icon: <Truck size={18} />, children: [
      { label: 'Couriers', to: '/couriers', permModule: 'Couriers' },
      { label: 'Courier Pricing', to: '/courier-pricing', superAdminOnly: true },
      { label: 'Billing Plans', to: '/billing-plans', permModule: 'Billing Plans' },
      { label: 'Status Mapping', to: '/courier-mappings', superAdminOnly: true },
    ],
  },
  {
    label: 'Finance & Billing', icon: <IndianRupee size={18} />, children: [
      // Freight is a billing screen, so it lives here rather than under Courier Operations, and —
      // like Orders and Invoices — it splits B2C / B2B because the two are priced differently.
      // "All Freight" is the existing combined view; every leaf is the one Freight permission.
      {
        label: 'Freight', icon: <Calculator size={18} />, permModule: 'Freight', children: [
          { label: 'Freight B2C', to: '/freight-b2c', permModule: 'Freight' },
          { label: 'Freight B2B', to: '/freight-b2b', permModule: 'Freight' },
          { label: 'All Freight', to: '/freight', permModule: 'Freight' },
        ],
      },
      { label: 'Invoices', to: '/invoices', permModule: 'Invoices' },
      { label: 'Credit Notes', to: '/credit-notes', permModule: 'Credit Notes' },
      { label: 'Debit Notes', to: '/debit-notes', permModule: 'Debit Notes' },
      { label: 'Ledger', to: '/ledger', permModule: 'Ledger' },
      { label: 'Remittance', to: '/remittance' },
      { label: 'Wallet & Recharges', to: '/wallet', permModule: 'Wallet' },
      // Platform-wide view: every merchant's movements in one place, with filters + export.
      { label: 'Wallet Ledger', to: '/wallet-ledger', permModule: 'Wallet' },
      { label: 'Price Calculator', to: '/price-calculator' },
    ],
  },
  {
    label: 'Lead Management', icon: <Target size={18} />, permModule: 'Leads', children: [
      { label: 'Dashboard', to: '/leads/dashboard', icon: <LayoutDashboard size={18} />, permModule: 'Leads', permTab: 'Dashboard' },
      { label: 'Manage Leads', to: '/leads/manage', icon: <Users size={18} />, permModule: 'Leads', permTab: 'Manage Leads' },
      { label: 'Manage Followups', to: '/leads/followups', icon: <CalendarClock size={18} />, permModule: 'Leads', permTab: 'Followups' },
      { label: 'Reports', to: '/leads/reports', icon: <BarChart3 size={18} />, permModule: 'Leads', permTab: 'Reports' },
      { label: 'Lead Pipeline', to: '/leads/pipeline', icon: <Columns3 size={18} />, permModule: 'Leads', permTab: 'Pipeline' },
      { label: 'Calendar', to: '/leads/calendar', icon: <CalendarDays size={18} />, permModule: 'Leads', permTab: 'Calendar' },
      { label: 'Trash', to: '/leads/trash', icon: <Trash2 size={18} />, permModule: 'Leads', permTab: 'Trash' },
      { label: 'Lead Settings', to: '/leads/settings', icon: <Settings size={18} />, permModule: 'Leads', permTab: 'Settings', adminOnly: true },
    ],
  },
  {
    label: 'Stakeholders & HR', icon: <Users size={18} />, children: [
      { label: 'Merchants', to: '/merchants', permModule: 'Merchants' },
      // Website / portal registrations waiting for review — see SignupRequestsPage.
      { label: 'Signup Requests', to: '/signup-requests', permModule: 'Signup Requests', grantOnly: true },
      { label: 'Customers', to: '/customers' },
      // ADMIN as well as SUPER_ADMIN. Every /users endpoint already accepts ADMIN — list, create,
      // update and delete — so this flag was the only thing hiding a page the API was happy to
      // serve them. Note the `permModule` cannot rescue an admin on its own: `isRestricted()`
      // returns false for SUPER_ADMIN/ADMIN, so admins bypass the permission matrix entirely and
      // granting one the 'Users' permission has no effect at all. The userType is the gate here.
      { label: 'Users', to: '/users', adminOnly: true, permModule: 'Users' },
      { label: 'Roles & Permissions', to: '/roles', superAdminOnly: true, permModule: 'Roles' },
      { label: 'HR', to: '/hr', permModule: 'HR' },
    ],
  },
  {
    label: 'System & Developer Tools', icon: <Settings size={18} />, children: [
      { label: 'Settings', to: '/settings' },
      { label: 'Masters', to: '/masters', permModule: 'Masters' },
      { label: 'Credentials', to: '/credentials', superAdminOnly: true },
      { label: 'API Keys', to: '/api-keys' },
      { label: 'Sales Channels', to: '/channels' },
      { label: 'Courier Webhook', to: '/courier-webhooks', permModule: 'Courier Webhook' },
      { label: 'Courier API Logs', to: '/courier-api-logs' },
      { label: 'Cron History', to: '/cron-history', adminOnly: true },
    ],
  },
  { label: 'Notifications', to: '/notifications', icon: <Mail size={18} />, permModule: 'Notifications' },
  // Deliberately has NO permModule: guidance on how the panel works must be reachable by
  // everyone, including a new user whose role has barely any modules ticked yet.
  { label: 'Help', to: '/help', icon: <BookOpen size={18} /> },
];

// Route path → permission module, for blocking direct-URL access to a module a
// restricted user wasn't granted. Dashboard & Settings are intentionally omitted
// (never gated) so no user is ever locked out of their landing page or own profile.
const ROUTE_PERM: { prefix: string; module: string }[] = [
  { prefix: '/user-dashboard', module: 'User Dashboard' },
  { prefix: '/pickup-requests', module: 'Pickup Requests' },
  { prefix: '/warehouses', module: 'Pickup Locations' },
  { prefix: '/orders-b2c', module: 'Orders B2C' },
  { prefix: '/orders-b2b', module: 'Orders B2B' },
  { prefix: '/gaint-ofd', module: 'Gaint OFD Orders' },
  { prefix: '/gaint-appointments', module: 'Gaint Appointments' },
  { prefix: '/rto-orders', module: 'RTO Orders' },
  { prefix: '/master-data', module: 'Master Data' },
  { prefix: '/tracking', module: 'Tracking' },
  { prefix: '/couriers', module: 'Couriers' },
  { prefix: '/billing-plans', module: 'Billing Plans' },
  { prefix: '/courier-webhooks', module: 'Courier Webhook' },
  { prefix: '/serviceability', module: 'Serviceability' },
  { prefix: '/merchants', module: 'Merchants' },
  { prefix: '/signup-requests', module: 'Signup Requests' },
  { prefix: '/branches', module: 'Branches' },
  { prefix: '/masters', module: 'Masters' },
  { prefix: '/credit-notes', module: 'Credit Notes' },
  { prefix: '/debit-notes', module: 'Debit Notes' },
  { prefix: '/ndr', module: 'NDR' },
  { prefix: '/ivr', module: 'IVR' },
  { prefix: '/invoices', module: 'Invoices' },
  { prefix: '/ledger', module: 'Ledger' },
  // Matching is exact-or-below, so the B2C/B2B paths need their own rows — '/freight' does not cover them.
  { prefix: '/freight-b2c', module: 'Freight' },
  { prefix: '/freight-b2b', module: 'Freight' },
  { prefix: '/freight', module: 'Freight' },
  { prefix: '/wms', module: 'Warehouses' },
  { prefix: '/weight', module: 'Weight Discrepancy' },
  // Typing /pod straight into the address bar is gated the same as the sidebar entry. Merchants are
  // unaffected — `canModule` lets merchant identities through, and their portal only ever offers the
  // single-shipment download.
  { prefix: '/pod', module: 'Bulk POD' },
  { prefix: '/b2b-docs', module: 'Bulk POD' },
  { prefix: '/hr', module: 'HR' },
  { prefix: '/leads', module: 'Leads' },
  { prefix: '/wallet-ledger', module: 'Wallet' },
  { prefix: '/wallet', module: 'Wallet' },
  { prefix: '/notifications', module: 'Notifications' },
  { prefix: '/users', module: 'Users' },
  { prefix: '/roles', module: 'Roles' },
];

const merchantNav: NavEntry[] = [
  { label: 'Dashboard', to: '/dashboard', icon: <LayoutDashboard size={18} /> },
  {
    label: 'Orders', icon: <Package size={18} />, children: [
      { label: 'Orders B2C', to: '/orders-b2c' },
      { label: 'Orders B2B', to: '/orders-b2b' },
      { label: 'Orders Bucket', to: '/orders-bucket' },
      { label: 'RTO Orders', to: '/rto-orders' },
      { label: 'Tracking', to: '/tracking' },
    ],
  },
  { label: 'Credit Notes', to: '/credit-notes', icon: <Receipt size={18} /> },
  { label: 'Debit Notes', to: '/debit-notes', icon: <Receipt size={18} /> },
  { label: 'NDR', to: '/ndr', icon: <AlertTriangle size={18} /> },
  { label: 'Invoices', to: '/invoices', icon: <FileText size={18} /> },
  { label: 'Ledger', to: '/ledger', icon: <FileText size={18} /> },
  { label: 'Freight', to: '/freight', icon: <Calculator size={18} /> },
  { label: 'Price Calculator', to: '/price-calculator', icon: <Calculator size={18} /> },
  {
    // Merchants get the read-only slice of WMS (WmsPage redirects any other section to the dashboard).
    label: 'WMS', icon: <Boxes size={18} />, children: [
      { label: 'Dashboard', to: '/wms/dashboard' },
      { label: 'Products', to: '/wms/products' },
      { label: 'Inventory', to: '/wms/inventory' },
      { label: 'GRN (Inbound)', to: '/wms/inbound/grn' },
      { label: 'Dispatch', to: '/wms/outbound/dispatch' },
      { label: 'Returns', to: '/wms/returns/rto-inward' },
      { label: 'Inventory Ageing', to: '/wms/reports/ageing' },
    ],
  },
  { label: 'Remittance', to: '/remittance', icon: <Banknote size={18} /> },
  { label: 'API Keys', to: '/api-keys', icon: <Code size={18} /> },
  { label: 'Weight', to: '/weight', icon: <Scale size={18} /> },
  { label: 'Customers', to: '/customers', icon: <UserCircle2 size={18} /> },
  { label: 'Wallet', to: '/wallet', icon: <Wallet size={18} /> },
  { label: 'Sales Channels', to: '/channels', icon: <ShoppingBag size={18} /> },
  { label: 'Settings', to: '/settings', icon: <Settings size={18} /> },
  { label: 'Help', to: '/help', icon: <BookOpen size={18} /> },
];

// A leaf is active when the current path equals it or sits beneath it.
function leafActive(to: string, pathname: string) { return pathname === to || pathname.startsWith(to + '/'); }
function groupHasActive(group: NavGroup, pathname: string): boolean {
  return leafDescendants(group).some(l => leafActive(l.to, pathname));
}
// Short label for the collapsed (icon-only) rail.
const collapsedLabel = (s: string) => s.replace('Orders ', '').replace('Tracking', 'TRK').replace('Roles & Permissions', 'Roles');

// Filter the nav tree recursively, dropping entries the user can't see and any group left empty.
function filterNav(entries: NavEntry[], allowed: (e: NavEntry) => boolean): NavEntry[] {
  return entries
    .filter(allowed)
    .map(e => isGroup(e) ? { ...e, children: filterNav(e.children, allowed) } : e)
    .filter(e => !isGroup(e) || e.children.length > 0);
}

function NavGroupItem({ group, collapsed }: { group: NavGroup; collapsed: boolean }) {
  const location = useLocation();
  const active = groupHasActive(group, location.pathname);
  const [open, setOpen] = useState(active);
  useEffect(() => { if (active) setOpen(true); }, [active]);

  if (collapsed) {
    // When collapsed, flatten every descendant leaf into short icon-rail links under a tooltip header.
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-center py-1 text-slate-400" title={group.label}>{group.icon}</div>
        {leafDescendants(group).map(c => (
          <NavLink key={c.to} to={c.to} title={c.label}
            className={({ isActive }) =>
              `block text-center truncate px-0.5 py-1.5 rounded text-[10px] ${isActive ? 'bg-primary-50 text-primary' : 'text-slate-500 hover:bg-slate-100'}`}>
            {collapsedLabel(c.label)}
          </NavLink>
        ))}
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${active ? 'text-primary' : 'text-slate-600 hover:bg-slate-100'}`}>
        {group.icon ?? <span className="w-[18px] shrink-0" />}
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="mt-1 ml-3 pl-3 border-l border-slate-200 space-y-1">
          {group.children.map((c, i) => isGroup(c)
            ? <NavGroupItem key={`g-${i}-${c.label}`} group={c} collapsed={collapsed} />
            : (
              <NavLink key={c.to} to={c.to}
                className={({ isActive }) =>
                  `block px-3 py-1.5 rounded-md text-sm transition ${isActive ? 'bg-primary-50 text-primary font-medium' : 'text-slate-600 hover:bg-slate-100'}`}>
                {c.label}
              </NavLink>
            )
          )}
        </div>
      )}
    </div>
  );
}

export function AppLayout({ portal }: { portal: 'admin' | 'merchant' }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sitemapOpen, setSitemapOpen] = useState(false);
  const [navQuery, setNavQuery] = useState('');
  const location = useLocation();
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);
  const { user, logout } = useAuth();
  const impersonation = useAuth((s) => s.impersonation);
  const stopImpersonation = useAuth((s) => s.stopImpersonation);
  const navigate = useNavigate();
  const baseNav = portal === 'admin' ? adminNav : merchantNav;
  const isSuperAdmin = user?.kind === 'user' && user.userType === 'SUPER_ADMIN';
  // `adminOnly` = SUPER_ADMIN *or* ADMIN (vs `superAdminOnly`, which is SUPER_ADMIN alone).
  const isAdmin = user?.kind === 'user' && (user.userType === 'SUPER_ADMIN' || user.userType === 'ADMIN');
  // An entry is visible if: not superAdminOnly (or you are super admin) AND not adminOnly (or you are
  // an admin) AND (no permModule, or your role grants view on it — SUPER_ADMIN/ADMIN always pass).
  const allowed = (e: any) =>
    (!e.superAdminOnly || isSuperAdmin) && (!e.adminOnly || isAdmin) && navAllowed(user, e.permModule)
    // Sales-only entries: the people who own merchant accounts, plus admins so they can check it.
    && (!e.salesOnly || isAdmin || user?.userType === 'SALES_PERSON')
    // A tab-wise entry needs its own tab granted, not just the module.
    && (!e.permTab || !isRestricted(user) || canModuleTab(user, e.permModule!, e.permTab))
    && (!e.grantOnly || isAdmin || canModuleTab(user, e.permModule!, 'All'));
  const nav: NavEntry[] = filterNav(baseNav, allowed);

  // Block direct-URL access to a permission module the (restricted) user wasn't granted.
  const blockedModule = (() => {
    if (portal !== 'admin' || !isRestricted(user)) return null;
    const hit = ROUTE_PERM.find(r => location.pathname === r.prefix || location.pathname.startsWith(r.prefix + '/'));
    return hit && !canModule(user, hit.module) ? hit.module : null;
  })();

  const displayName = user?.kind === 'merchant'
    ? user.companyName
    : `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email;

  const { data: brand } = useQuery({
    queryKey: ['super-admin-profile'],
    queryFn: async () => {
      try { return (await api.get('/settings/super_admin_profile')).data as any; }
      catch { return {}; }
    },
    staleTime: 5 * 60 * 1000,
  });

  // Active-ticket count for the sidebar badge (silently null if the user lacks the Tickets permission).
  const { data: ticketStats } = useQuery<any>({
    queryKey: ['ticket-stats-nav'],
    queryFn: async () => { try { return (await api.get('/tickets/stats')).data; } catch { return null; } },
    refetchInterval: 60000, staleTime: 30000,
  });
  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside data-mobile-open={mobileNavOpen}
        className={`${collapsed ? 'w-16' : 'w-60'} bg-white border-r border-slate-200 flex flex-col transition-all shrink-0`}>
        <div className="h-14 flex items-center justify-between px-4 border-b border-slate-200">
          {!collapsed && (
            <div className="flex items-center gap-2">
              {brand?.logoUrl ? (
                <img src={brand.logoUrl} alt="logo" style={{ height: 28, maxWidth: 120, objectFit: 'contain' }} />
              ) : (
                <>
                  <div className="bg-primary text-white p-1.5 rounded-md"><Truck size={16} /></div>
                  <span className="font-bold text-slate-900">{brand?.brandName ?? brand?.companyName ?? 'Courier'}</span>
                </>
              )}
            </div>
          )}
          <button onClick={() => setCollapsed(!collapsed)} className="text-slate-400 hover:text-slate-600">
            <ChevronLeft size={18} className={`transition-transform ${collapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {!collapsed && (
          <div className="px-3 pt-3">
            <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-2.5 py-1.5 bg-slate-50 focus-within:bg-white focus-within:border-slate-300 transition">
              <Search size={14} className="text-slate-400 shrink-0" />
              <input value={navQuery} onChange={(e) => setNavQuery(e.target.value)} placeholder="Search menu…"
                className="flex-1 bg-transparent outline-none text-sm" />
              {navQuery && <button onClick={() => setNavQuery('')} title="Clear" className="text-slate-400 hover:text-slate-600 text-xs">✕</button>}
            </div>
          </div>
        )}

        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {(() => {
            const term = navQuery.trim().toLowerCase();
            // Type-to-find: flatten the (already permission-filtered) nav to its leaf pages and show matches.
            if (term && !collapsed) {
              const leaves = nav.flatMap(leafDescendants).filter((l) => l.label.toLowerCase().includes(term));
              if (leaves.length === 0) return <div className="px-3 py-6 text-xs text-slate-400 text-center">No menu items match “{navQuery}”.</div>;
              return leaves.map((l) => (
                <NavLink key={l.to} to={l.to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${isActive ? 'bg-primary-50 text-primary' : 'text-slate-600 hover:bg-slate-100'}`}>
                  {l.icon ?? <span className="w-[18px] shrink-0" />}
                  <span className="truncate">{l.label}</span>
                </NavLink>
              ));
            }
            return nav.map((entry, idx) => isGroup(entry)
              ? <NavGroupItem key={`g-${idx}-${entry.label}`} group={entry} collapsed={collapsed} />
              : (
                <NavLink key={entry.to} to={entry.to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${isActive ? 'bg-primary-50 text-primary' : 'text-slate-600 hover:bg-slate-100'}`}>
                  {entry.icon}
                  {!collapsed && <span className="flex-1">{entry.label}</span>}
                  {!collapsed && entry.to === '/tickets' && (ticketStats?.active ?? 0) > 0 && (
                    <span className="ml-auto bg-primary text-white text-[10px] rounded-full px-1.5 min-w-[18px] text-center leading-4">{ticketStats.active}</span>
                  )}
                </NavLink>
              )
            );
          })()}
        </nav>

        <div className="border-t border-slate-200 p-2">
          <button onClick={() => logout()} className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
            <LogOut size={18} />
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </aside>

      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setMobileNavOpen(false)} />
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-3 md:px-6 gap-2">
          <button onClick={() => setMobileNavOpen(true)}
            className="md:hidden p-1.5 rounded-lg hover:bg-slate-100 text-slate-600">
            <Menu size={20} />
          </button>
          <GlobalSearch />
          <div className="flex items-center gap-3">
            {/* Merchant portal only. On the admin side this pill is the NET position across every
                merchant on the platform — a figure nobody acts on from the header, and it was the
                slowest thing in it. The same numbers are still on Finance → Wallet for anyone who
                wants them. */}
            {portal !== 'admin' && <WalletBalanceMenu portal={portal} />}
            {/* Scan from any screen — same dialog as Tracking → Scan, without navigating there.
                Internal only, and only for users whose role grants the Tracking module. */}
            {portal === 'admin' && navAllowed(user, 'Tracking') && <QuickScanButton />}
            {portal === 'admin' && <MarkAttendanceButton />}
            <a href="https://trk.gaintlogistic.com" target="_blank" rel="noreferrer" title="Public tracking"
              className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-blue-700 text-xs font-medium transition">
              <Truck size={14} /> Track
            </a>
            <a href={`${API_BASE_URL}/download/apk/internal`} target="_blank" rel="noreferrer" title="Download Android App"
              className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 rounded-lg text-emerald-700 text-xs font-medium transition">
              <Smartphone size={14} /> Download App
            </a>
            <button onClick={() => setSitemapOpen(true)} title="Site Map — all pages &amp; links"
              className="text-slate-400 hover:text-primary p-1.5 rounded-lg hover:bg-slate-100">
              <SiteMapIcon size={18} />
            </button>
            <button className="text-slate-400 hover:text-slate-600 relative">
              <Bell size={18} />
              <span className="absolute -top-1 -right-1 bg-accent text-[10px] text-black font-bold w-4 h-4 rounded-full flex items-center justify-center">3</span>
            </button>
            <UserMenu displayName={displayName} user={user} portal={portal} onLogout={() => logout()} onNavigate={navigate} />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          {impersonation && (
            <div className="sticky top-0 z-30 flex items-center gap-3 px-4 py-2 bg-amber-500 text-white text-sm">
              <LogIn size={15} />
              <span>You are logged in as <b>{impersonation.name}</b> — viewing the app with their permissions.</span>
              <button onClick={() => stopImpersonation()} className="ml-auto px-3 py-1 bg-white/20 hover:bg-white/30 rounded font-medium">Exit &amp; return to admin</button>
            </div>
          )}
          {blockedModule ? <AccessDenied module={blockedModule} /> : <Outlet />}
        </main>
      </div>
    <ChatbotWidget />
    {sitemapOpen && (
      <SiteMapModal nav={nav} fullNav={baseNav} onClose={() => setSitemapOpen(false)}
        onNavigate={(p) => { setSitemapOpen(false); navigate(p); }} />
    )}
    </div>
  );
}

// Full-page index of every link the current user can reach (built from the permission-filtered nav),
// plus the external links from the header. Searchable; each row navigates and shows its URL.
function SiteMapModal({ nav, fullNav, onClose, onNavigate }: {
  nav: NavEntry[];
  /** The nav BEFORE permission filtering — used to show what exists but is not granted. */
  fullNav: NavEntry[];
  onClose: () => void;
  onNavigate: (path: string) => void;
}) {
  const [q, setQ] = useState('');

  // One card per top-level module; its sub-menus become name-only chips (all leaf descendants).
  const sections: { title: string; icon?: React.ReactNode; links: NavLeaf[] }[] = [];
  const standalone: NavLeaf[] = [];
  for (const e of nav) {
    if (isGroup(e)) sections.push({ title: e.label, icon: e.icon, links: leafDescendants(e) });
    else standalone.push(e);
  }
  if (standalone.length) sections.unshift({ title: 'General', icon: <SiteMapIcon size={18} />, links: standalone });

  // Modules that EXIST but this account has not been granted.
  //
  // The map used to be built purely from the permission-filtered nav, so a page you lacked access
  // to was simply absent — indistinguishable from a page that does not exist, which is what "not
  // all the modules are showing" looks like from the outside. They are listed here, greyed and not
  // clickable, so the map is a complete picture of the platform and anyone missing something can
  // see it exists and ask for access. Nothing is reachable from here that the sidebar and the
  // route guard would not already refuse.
  const grantedTargets = new Set(nav.flatMap(leafDescendants).map(l => l.to));
  const locked = fullNav
    .flatMap(leafDescendants)
    .filter(l => !grantedTargets.has(l.to));

  const external = [
    { label: 'Public Tracking', to: 'https://trk.gaintlogistic.com' },
    { label: 'Download Android App', to: `${API_BASE_URL}/download/apk/internal` },
  ];

  const needle = q.trim().toLowerCase();
  const match = (label: string, to: string) => !needle || `${label} ${to}`.toLowerCase().includes(needle);
  const filtered = sections
    .map(s => ({ ...s, links: s.links.filter(l => match(l.label, l.to)) }))
    .filter(s => s.links.length);
  const extFiltered = external.filter(l => match(l.label, l.to));
  const lockedFiltered = locked.filter(l => match(l.label, l.to));
  const totalCount = sections.reduce((n, s) => n + s.links.length, 0);

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <SiteMapIcon size={18} className="text-primary" /> Site Map
            <span className="text-xs font-normal text-slate-400">
              — {sections.length} modules · {totalCount} pages you can access
              {locked.length > 0 && <> · {locked.length} need access</>}. Tap any item to open it.
            </span>
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
        </div>
        <div className="px-5 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2">
            <Search size={16} className="text-slate-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pages…"
              className="flex-1 bg-transparent outline-none text-sm" />
          </div>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-h-[70vh] overflow-y-auto bg-slate-50/50">
          {filtered.map(section => (
            <div key={section.title} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  {section.icon && <span className="text-primary shrink-0">{section.icon}</span>}
                  <h3 className="font-semibold text-slate-900 text-sm leading-snug">{section.title}</h3>
                </div>
                <span className="shrink-0 text-[11px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-full min-w-[24px] h-6 px-1.5 flex items-center justify-center">{section.links.length}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {section.links.map(l => (
                  <button key={l.to} onClick={() => onNavigate(l.to)} title={l.label}
                    className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 text-xs font-medium hover:border-primary hover:text-primary hover:bg-primary-50 transition">
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {extFiltered.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-primary shrink-0"><ExternalLink size={18} /></span>
                  <h3 className="font-semibold text-slate-900 text-sm leading-snug">External</h3>
                </div>
                <span className="shrink-0 text-[11px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-full min-w-[24px] h-6 px-1.5 flex items-center justify-center">{extFiltered.length}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {extFiltered.map(l => (
                  <a key={l.to} href={l.to} target="_blank" rel="noreferrer" title={l.label}
                    className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 text-xs font-medium hover:border-primary hover:text-primary hover:bg-primary-50 transition inline-flex items-center gap-1">
                    {l.label} <ExternalLink size={11} />
                  </a>
                ))}
              </div>
            </div>
          )}
          {lockedFiltered.length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-slate-400 shrink-0"><Lock size={18} /></span>
                  <h3 className="font-semibold text-slate-900 text-sm leading-snug">Needs access</h3>
                </div>
                <span className="shrink-0 text-[11px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-full min-w-[24px] h-6 px-1.5 flex items-center justify-center">{lockedFiltered.length}</span>
              </div>
              <p className="text-[11px] text-slate-500 mb-2 leading-snug">
                These pages exist but are not switched on for your login. Ask an administrator to grant them.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {lockedFiltered.map(l => (
                  <span key={l.to} title={`${l.label} — you do not have access`}
                    className="px-2.5 py-1.5 rounded-lg border border-dashed border-slate-200 bg-slate-50 text-slate-400 text-xs font-medium inline-flex items-center gap-1 cursor-not-allowed">
                    <Lock size={10} /> {l.label}
                  </span>
                ))}
              </div>
            </div>
          )}
          {filtered.length === 0 && extFiltered.length === 0 && (
            <div className="col-span-full text-center text-sm text-slate-400 py-8">No pages match "{q}".</div>
          )}
        </div>
      </div>
    </div>
  );
}

function AccessDenied({ module }: { module: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-6 py-20">
      <div className="bg-amber-50 border border-amber-200 rounded-full p-4 mb-4">
        <KeyRound size={28} className="text-amber-500" />
      </div>
      <h2 className="text-lg font-semibold text-slate-900">You don't have access to {module}</h2>
      <p className="text-sm text-slate-500 mt-1 max-w-md">
        This section isn't part of your permissions. If you need it, ask an administrator to grant you access
        under Users → Roles &amp; Permissions.
      </p>
    </div>
  );
}

function UserMenu({ displayName, user, portal, onLogout, onNavigate }: {
  displayName?: string | null;
  user: any;
  portal: 'admin' | 'merchant';
  onLogout: () => void;
  onNavigate: (p: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const fn = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as any)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-slate-100">
        <div className="w-7 h-7 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">
          {(displayName ?? '?').charAt(0).toUpperCase()}
        </div>
        <div className="text-left hidden md:block">
          <div className="text-sm font-medium text-slate-900">{displayName}</div>
          <div className="text-xs text-slate-500">{user?.userType ?? user?.kind}</div>
        </div>
        <ChevronDown size={14} className={`text-slate-400 transition hidden md:block ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-60 bg-white border border-slate-200 rounded-lg shadow-xl py-2 z-50">
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-900 truncate">{displayName}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">{user?.userType ?? user?.kind}{user?.empCode ? ` · ${user.empCode}` : ''}</div>
          </div>
          <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
            <span className="text-xs text-slate-500">Theme</span>
            <ThemeToggle />
          </div>
          <button onClick={() => { setOpen(false); onNavigate('/settings'); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
            <Settings size={14} /> My Profile &amp; Settings
          </button>
          {portal === 'admin' && (
            <button onClick={() => { setOpen(false); onNavigate('/settings'); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
              <KeyRound size={14} /> Change Password
            </button>
          )}
          <div className="border-t border-slate-100 mt-1 pt-1">
            <button onClick={() => { setOpen(false); onLogout(); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
